import { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Button } from "react-bootstrap";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import CustomInput from "./CustomInput";
import CustomTextArea from "./CustomTextArea";
import { useGetOrganizationStats } from "../../hooks/organization/useGetOrganizationStats";

/**
 * @typedef {Object} LicenseModalProps
 * @property {boolean} showModal - Controls the visibility of the modal
 * @property {() => void} handleClose - Function to close the modal
 * @property {"add" | "disable"} type - Type of modal (add or disable licenses)
 * @property {string} schoolName - Name of the school
 * @property {number} schoolId - ID of the school
 * @property {number} currentLicenses - Current assigned licenses
 * @property {boolean} loading - Loading state
 * @property {string} error - Error message
 * @property {(data: object, onSuccess: () => void) => Promise<boolean>} onSubmit - Submit handler
 * @property {() => void} onSuccess - Success callback
 */

/**
 * A reusable modal component for adding or disabling licenses.
 */
const LicenseModal = ({
    showModal,
    handleClose,
    type = "add",
    schoolName = "",
    schoolId,
    currentLicenses = 0,
    loading = false,
    error = "",
    onSubmit,
    onSuccess,
}) => {
    const [submitSuccess, setSubmitSuccess] = useState(false);

    const isAddMode = type === "add";

    // Fetch organization stats to get available licenses
    const {
        stats: orgStats,
        loading: statsLoading,
        getOrganizationStats,
    } = useGetOrganizationStats();

    const availableLicenses = orgStats?.licenses?.remaining ?? 0;

    // Fetch stats when modal opens in add mode
    useEffect(() => {
        if (showModal && isAddMode) {
            getOrganizationStats();
        }
    }, [showModal, isAddMode]);

    const title = isAddMode ? "Add Licenses" : "Disable Licenses";
    const submitButtonText = isAddMode ? "Add Licenses" : "Disable Licenses";
    const successMessage = isAddMode
        ? "Licenses have been successfully added to the school."
        : "Licenses have been successfully disabled for the school.";

    const maxAddLicenses = Math.min(availableLicenses, 10000);

    const validationSchema = Yup.object({
        licenseCount: Yup.number()
            .required("Please enter number of licenses")
            .min(1, "At least 1 license is required")
            .max(
                isAddMode ? maxAddLicenses : currentLicenses,
                isAddMode
                    ? availableLicenses > 10000
                        ? "Cannot assign more than 10,000 licenses in a single request"
                        : `Cannot add more than ${availableLicenses} licenses (available in organization)`
                    : `Cannot disable more than ${currentLicenses} licenses`,
            ),
        remarks: Yup.string().max(500, "Remarks cannot exceed 500 characters"),
    });

    const formik = useFormik({
        initialValues: {
            licenseCount: "",
            remarks: "",
        },
        validationSchema,
        onSubmit: async (values, { resetForm }) => {
            const data = {
                sub_school_institution_id: schoolId,
                license_count: parseInt(values.licenseCount),
                ...(isAddMode
                    ? { remark: values.remarks }
                    : { remarks: values.remarks }),
            };

            const success = await onSubmit(data, () => {
                setSubmitSuccess(true);
                resetForm();
            });

            if (success) {
                setSubmitSuccess(true);
                resetForm();
            }
        },
    });

    const handleModalClose = () => {
        formik.resetForm();
        setSubmitSuccess(false);
        handleClose();
    };

    const handleContinue = () => {
        setSubmitSuccess(false);
        if (onSuccess) onSuccess();
        handleModalClose();
    };

    // Success state UI
    if (submitSuccess) {
        return (
            <Modal
                show={showModal}
                onHide={handleModalClose}
                centered
                className="custom-modal"
                backdrop="static"
                keyboard={false}
            >
                <Modal.Body className="p-2 m-4">
                    <div className="text-center mb-2">
                        <h5 className="text-success fw-bold fs-3">Success</h5>
                    </div>
                    <p className="text-center mb-4 mt-2 pt-2 custom-modal-description">
                        {successMessage}
                    </p>
                    <div className="d-flex justify-content-center">
                        <Button className="custom-primary-button" onClick={handleContinue}>
                            Continue
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        );
    }

    return (
        <Modal
            show={showModal}
            onHide={handleModalClose}
            centered
            className="custom-modal"
            backdrop="static"
            keyboard={false}
        >
            <Modal.Body className="p-2 m-4">
                {/* Title */}
                <div className="text-center mb-3">
                    <h5
                        className={`fw-bold fs-3 ${isAddMode ? "text-success" : "text-danger"}`}
                    >
                        {title}
                    </h5>
                </div>

                {/* School Name */}
                <p className="text-center mb-4 text-muted">
                    School: <strong>{schoolName}</strong>
                </p>

                {/* Form */}
                <FormikProvider value={formik}>
                    <form onSubmit={formik.handleSubmit}>
                        <div className="mb-3">
                            <CustomInput
                                label={
                                    isAddMode
                                        ? "Number of Licenses to Add"
                                        : "Number of Licenses to Disable"
                                }
                                name="licenseCount"
                                type="number"
                                placeholder="Enter number of licenses"
                                disabled={loading}
                            />
                            {isAddMode && (
                                <div style={{
                                    backgroundColor: "#e8f4fd",
                                    border: "1px solid #b3d9f2",
                                    borderRadius: "6px",
                                    padding: "8px 12px",
                                    marginTop: "8px",
                                    fontSize: "13px",
                                    color: "#1a6fa8",
                                }}>
                                    <i className="fa-solid fa-circle-info me-2"></i>
                                    {statsLoading
                                        ? "Checking available licenses..."
                                        : `Available licenses in organization: ${availableLicenses}`}
                                </div>
                            )}
                            {!isAddMode && currentLicenses > 0 && (
                                <div style={{
                                    backgroundColor: "#fff3e0",
                                    border: "1px solid #ffe0b2",
                                    borderRadius: "6px",
                                    padding: "8px 12px",
                                    marginTop: "8px",
                                    fontSize: "13px",
                                    color: "#e65100",
                                }}>
                                    <i className="fa-solid fa-circle-info me-2"></i>
                                    Maximum available to disable: {currentLicenses}
                                </div>
                            )}
                        </div>

                        <div className="mb-4">
                            <CustomTextArea
                                label="Remarks (Optional)"
                                name="remarks"
                                placeholder="Enter remarks"
                                showAstreisk={false}
                                disabled={loading}
                            />
                        </div>

                        {/* Error Display */}
                        {error && (
                            <div className="alert alert-danger mb-3" role="alert">
                                {error}
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="d-flex flex-column flex-md-row gap-3 justify-content-center">
                            <button
                                type="button"
                                className="custom-cancel-button"
                                onClick={handleModalClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <Button
                                type="submit"
                                className="custom-primary-button"
                                disabled={loading || (isAddMode && statsLoading)}
                            >
                                {loading ? "Processing..." : submitButtonText}
                            </Button>
                        </div>
                    </form>
                </FormikProvider>
            </Modal.Body>
        </Modal>
    );
};

export default LicenseModal;
